"use client";

import { c as _c } from "react/compiler-runtime";
import { ErrorStatusBadges } from "@/components/errors/ErrorCategoryBadge";
import { getPrimaryDisplayMessage, resolveErrorInfo, UnifiedErrorPanel, type UnifiedErrorPanelProps } from "@/components/errors/UnifiedErrorPanel";
import { NodeIconTile } from "@/components/ui/node-icon-tile";
import { useResolvedNodeVisual } from "@/hooks/useResolvedNodeVisual";
import { cn } from "@/lib/utils";
import type { ReactNode } from "react";
interface FailedNodeInfo {
  name?: string;
  type?: string;
  description?: string;
  category?: "action" | "trigger" | "logic";
}
interface FailedNodeErrorSectionProps extends Pick<UnifiedErrorPanelProps, "nodeState" | "nodeConfig" | "workflowId" | "runId" | "variant" | "showAIAnalysis" | "showFieldEditor" | "showVersionCompare" | "showFixWithAI" | "showGetSupport" | "onFixWithAI" | "onFieldUpdate" | "onRollback"> {
  nodeInfo?: FailedNodeInfo;
  iconClassName?: string;
  className?: string;
  headerClassName?: string;
  bodyClassName?: string;
  titleClassName?: string;
  summaryClassName?: string;
  meta?: ReactNode;
}
export function getNodeCategoryColor(category?: FailedNodeInfo["category"]) {
  switch (category) {
    case "trigger":
      return "#2450FF";
    case "logic":
      return "#8b5cf6";
    case "action":
    default:
      return "#22c55e";
  }
}
export function FailedNodeErrorSection(t0) {
  const $ = _c(66);
  const {
    nodeInfo,
    nodeState,
    nodeConfig,
    workflowId,
    runId,
    variant,
    showAIAnalysis,
    showFieldEditor,
    showVersionCompare,
    showFixWithAI,
    showGetSupport,
    onFixWithAI,
    onFieldUpdate,
    onRollback,
    iconClassName,
    className,
    headerClassName,
    bodyClassName,
    titleClassName,
    summaryClassName,
    meta
  } = t0;
  const nodeVisual = useResolvedNodeVisual(nodeInfo?.type);
  const nodeLabel = nodeInfo?.name || nodeInfo?.type || "Node";
  let t1;
  if ($[0] !== nodeState) {
    t1 = resolveErrorInfo(nodeState);
    $[0] = nodeState;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const errorInfo = t1;
  const t2 = nodeState?.error || "Unknown error";
  let t3;
  if ($[2] !== nodeState || $[3] !== t2) {
    t3 = getPrimaryDisplayMessage(nodeState, t2);
    $[2] = nodeState;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const primaryDisplayMessage = t3;
  let t4;
  if ($[5] !== headerClassName) {
    t4 = cn("flex items-start gap-6", headerClassName);
    $[5] = headerClassName;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = nodeInfo?.type || "action";
  const t6 = nodeInfo?.description || "";
  const t7 = nodeVisual.logoUrl;
  const t8 = nodeVisual.connector;
  const t9 = nodeInfo?.category;
  let t10;
  if ($[7] !== t9) {
    t10 = getNodeCategoryColor(t9);
    $[7] = t9;
    $[8] = t10;
  } else {
    t10 = $[8];
  }
  let t11;
  if ($[9] !== iconClassName || $[10] !== nodeLabel || $[11] !== nodeVisual.connector || $[12] !== nodeVisual.logoUrl || $[13] !== t10 || $[14] !== t5 || $[15] !== t6) {
    t11 = <NodeIconTile size="md" id={t5} subject={nodeLabel} context={t6} logoUrl={t7} connector={t8} wrapClassName={iconClassName} alt={nodeLabel} color={t10} />;
    $[9] = iconClassName;
    $[10] = nodeLabel;
    $[11] = nodeVisual.connector;
    $[12] = nodeVisual.logoUrl;
    $[13] = t10;
    $[14] = t5;
    $[15] = t6;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== titleClassName) {
    t12 = cn("truncate text-lg font-semibold tracking-tight text-red-600 dark:text-red-400", titleClassName);
    $[17] = titleClassName;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== nodeLabel || $[20] !== t12) {
    t13 = <h4 className={t12}>Failed at {nodeLabel}</h4>;
    $[19] = nodeLabel;
    $[20] = t12;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== errorInfo) {
    t14 = errorInfo && <ErrorStatusBadges errorInfo={errorInfo} />;
    $[22] = errorInfo;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t13 || $[25] !== t14) {
    t15 = <div className="flex flex-wrap items-center gap-2">{t13}{t14}</div>;
    $[24] = t13;
    $[25] = t14;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== summaryClassName) {
    t16 = cn("body-sm-medium mt-1 text-red-600 dark:text-red-400", summaryClassName);
    $[27] = summaryClassName;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== primaryDisplayMessage || $[30] !== t16) {
    t17 = <p className={t16}>{primaryDisplayMessage}</p>;
    $[29] = primaryDisplayMessage;
    $[30] = t16;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== errorInfo) {
    t18 = errorInfo?.remediation && <p className="body-sm text-ds-text-subtle dark:text-ds-text-disabled mt-1">{errorInfo.remediation}</p>;
    $[32] = errorInfo;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== meta || $[35] !== t15 || $[36] !== t17 || $[37] !== t18) {
    t19 = <div className="min-w-0 flex-1">{t15}{t17}{t18}{meta}</div>;
    $[34] = meta;
    $[35] = t15;
    $[36] = t17;
    $[37] = t18;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t11 || $[40] !== t19 || $[41] !== t4) {
    t20 = <div className={t4}>{t11}{t19}</div>;
    $[39] = t11;
    $[40] = t19;
    $[41] = t4;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  const t21 = nodeState?.error || "Unknown error";
  let t22;
  if ($[43] !== nodeConfig || $[44] !== nodeInfo || $[45] !== nodeState || $[46] !== onFieldUpdate || $[47] !== onFixWithAI || $[48] !== onRollback || $[49] !== runId || $[50] !== showAIAnalysis || $[51] !== showFieldEditor || $[52] !== showFixWithAI || $[53] !== showGetSupport || $[54] !== showVersionCompare || $[55] !== t21 || $[56] !== variant || $[57] !== workflowId) {
    t22 = <UnifiedErrorPanel error={t21} nodeState={nodeState} nodeInfo={nodeInfo} nodeConfig={nodeConfig} workflowId={workflowId} runId={runId} variant={variant} showAIAnalysis={showAIAnalysis} showFieldEditor={showFieldEditor} showVersionCompare={showVersionCompare} showFixWithAI={showFixWithAI} showGetSupport={showGetSupport} onFixWithAI={onFixWithAI} onFieldUpdate={onFieldUpdate} onRollback={onRollback} />;
    $[43] = nodeConfig;
    $[44] = nodeInfo;
    $[45] = nodeState;
    $[46] = onFieldUpdate;
    $[47] = onFixWithAI;
    $[48] = onRollback;
    $[49] = runId;
    $[50] = showAIAnalysis;
    $[51] = showFieldEditor;
    $[52] = showFixWithAI;
    $[53] = showGetSupport;
    $[54] = showVersionCompare;
    $[55] = t21;
    $[56] = variant;
    $[57] = workflowId;
    $[58] = t22;
  } else {
    t22 = $[58];
  }
  let t23;
  if ($[59] !== bodyClassName || $[60] !== t22) {
    t23 = <div className={bodyClassName}>{t22}</div>;
    $[59] = bodyClassName;
    $[60] = t22;
    $[61] = t23;
  } else {
    t23 = $[61];
  }
  let t24;
  if ($[62] !== className || $[63] !== t20 || $[64] !== t23) {
    t24 = <div className={className}>{t20}{t23}</div>;
    $[62] = className;
    $[63] = t20;
    $[64] = t23;
    $[65] = t24;
  } else {
    t24 = $[65];
  }
  return t24;
}